import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Button, Modal, Form, Spinner } from "react-bootstrap";
import { createColumnHelper } from "@tanstack/react-table";
import TanstackTable from "@/components/TanstackTable";
import { FaultCodeApi } from "@/services/FaultCode.api";
import { FaultCode } from "@/shared/models/FaultCode";
import { useAppDispatch } from "@/redux/store";
import { setToastError, setToastSuccess } from "@/redux/shareSlice";
import { useCommonData } from "@/hooks/useCommonData";

// Fault Code Management Component
const FaultCodeManagement: React.FC = () => {
  const dispatch = useAppDispatch();
  const { reloadFaultCodes } = useCommonData();
  const [faultCodes, setFaultCodes] = useState<FaultCode[]>([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingCode, setEditingCode] = useState<FaultCode | null>(null);
  const [formData, setFormData] = useState<{
    code: string;
    isActive: boolean;
    workOrderInstructionsEU: string;
    workOrderInstructionsNA: string;
  }>({
    code: "",
    isActive: true,
    workOrderInstructionsEU: "",
    workOrderInstructionsNA: "",
  });

  const columnHelper = createColumnHelper<FaultCode>();

  const loadFaultCodes = useCallback(async () => {
    setLoading(true);
    try {
      const codes = await FaultCodeApi.getAllFaultCodes();
      setFaultCodes(codes);
    } catch (error) {
      dispatch(setToastError("Failed to load fault codes"));
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [dispatch]);

  useEffect(() => {
    loadFaultCodes();
  }, [loadFaultCodes]);

  const handleShowModal = useCallback((code?: FaultCode) => {
    if (code) {
      setEditingCode(code);
      setFormData({
        code: code.code,
        isActive: code.isActive,
        workOrderInstructionsEU: code.workOrderInstructionsEU,
        workOrderInstructionsNA: code.workOrderInstructionsNA,
      });
    } else {
      setEditingCode(null);
      setFormData({ code: "", isActive: true, workOrderInstructionsEU: "", workOrderInstructionsNA: "" });
    }
    setShowModal(true);
  }, []);

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingCode(null);
    setFormData({ code: "", isActive: true, workOrderInstructionsEU: "", workOrderInstructionsNA: "" });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.code.trim()) {
      dispatch(setToastError("Fault code is required"));
      return;
    }

    try {
      if (editingCode) {
        // Update existing
        const success = await FaultCodeApi.updateFaultCode({
          id: editingCode.id!,
          code: formData.code.trim(),
          isActive: formData.isActive,
          workOrderInstructionsEU: formData.workOrderInstructionsEU,
          workOrderInstructionsNA: formData.workOrderInstructionsNA,
        });
        if (success) {
          dispatch(setToastSuccess("Fault code updated successfully"));
          await reloadFaultCodes();
        } else {
          throw new Error("Update failed");
        }
      } else {
        // Create new
        const success = await FaultCodeApi.createFaultCode({
          code: formData.code.trim(),
          isActive: formData.isActive,
          workOrderInstructionsEU: formData.workOrderInstructionsEU,
          workOrderInstructionsNA: formData.workOrderInstructionsNA,
        });
        if (success) {
          dispatch(setToastSuccess("Fault code created successfully"));
          await reloadFaultCodes();
        } else {
          throw new Error("Create failed");
        }
      }
      handleCloseModal();
      loadFaultCodes();
    } catch (error) {
      dispatch(setToastError(`Failed to ${editingCode ? "update" : "create"} fault code`));
      console.error(error);
    }
  };

  const handleDelete = useCallback(
    async (code: FaultCode) => {
      if (!window.confirm(`Are you sure you want to delete "${code.code}"?`)) {
        return;
      }

      try {
        await FaultCodeApi.deleteFaultCode(code.id!);
        dispatch(setToastSuccess("Fault code deleted successfully"));
        await reloadFaultCodes();
        loadFaultCodes();
      } catch (error) {
        dispatch(setToastError("Failed to delete fault code"));
        console.error(error);
      }
    },
    [dispatch, loadFaultCodes, reloadFaultCodes]
  );

  const handleToggleActive = useCallback(
    async (code: FaultCode) => {
      try {
        const success = await FaultCodeApi.updateFaultCode({
          id: code.id!,
          code: code.code,
          isActive: !code.isActive,
          workOrderInstructionsEU: code.workOrderInstructionsEU,
          workOrderInstructionsNA: code.workOrderInstructionsNA,
        });
        if (success) {
          dispatch(setToastSuccess(`Fault code ${!code.isActive ? "activated" : "deactivated"}`));
          await reloadFaultCodes();
          loadFaultCodes();
        } else {
          throw new Error("Toggle failed");
        }
      } catch (error) {
        dispatch(setToastError("Failed to update fault code status"));
        console.error(error);
      }
    },
    [dispatch, loadFaultCodes, reloadFaultCodes]
  );

  const columns = useMemo(
    () => [
      columnHelper.accessor("id", {
        header: "ID",
        cell: (info) => info.getValue(),
        enableColumnFilter: false,
      }),
      columnHelper.accessor("code", {
        header: "Fault Code",
        cell: (info) => info.getValue(),
      }),
      columnHelper.accessor("isActive", {
        header: "Status",
        cell: (info) => (
          <span className={`status-badge ${info.getValue() ? "status-active" : "status-inactive"}`}>
            {info.getValue() ? "Active" : "Inactive"}
          </span>
        ),
        enableColumnFilter: false,
      }),
      columnHelper.display({
        id: "actions",
        header: "Actions",
        cell: (info) => {
          const code = info.row.original;
          return (
            <div className="d-flex gap-2">
              <Button variant="outline-primary" size="sm" onClick={() => handleShowModal(code)}>
                <i className="bi bi-pencil me-1"></i>
                Edit
              </Button>
              <Button
                variant={code.isActive ? "outline-warning" : "outline-success"}
                size="sm"
                onClick={() => handleToggleActive(code)}
              >
                <i className={`bi bi-toggle-${code.isActive ? "on" : "off"} me-1`}></i>
                {code.isActive ? "Deactivate" : "Activate"}
              </Button>
              <Button variant="outline-danger" size="sm" onClick={() => handleDelete(code)}>
                <i className="bi bi-trash me-1"></i>
                Delete
              </Button>
            </div>
          );
        },
        enableSorting: false,
        enableColumnFilter: false,
      }),
    ],
    [columnHelper, handleShowModal, handleToggleActive, handleDelete]
  );

  return (
    <div className="fault-code-management">
      <div className="action-buttons">
        <Button variant="primary" onClick={() => handleShowModal()}>
          <i className="bi bi-plus-circle"></i> Add New Fault Code
        </Button>
        <Button variant="secondary" onClick={loadFaultCodes} disabled={loading}>
          <i className="bi bi-arrow-clockwise"></i> Refresh
        </Button>
      </div>

      {loading ? (
        <div className="text-center py-5">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
        </div>
      ) : faultCodes.length === 0 ? (
        <div className="text-center py-4">
          <p>No fault codes found. Click "Add New Fault Code" to create one.</p>
        </div>
      ) : (
        <div className="fault-code-table">
          <TanstackTable data={faultCodes} columns={columns} paging={true} />
        </div>
      )}

      {/* Add/Edit Modal */}
      <Modal show={showModal} onHide={handleCloseModal}>
        <Modal.Header closeButton>
          <Modal.Title>{editingCode ? "Edit Fault Code" : "Add New Fault Code"}</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>
                Fault Code <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter fault code"
                value={formData.code}
                onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>
                WorkOrder Instructions EU <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter WorkOrder Instructions EU"
                value={formData.workOrderInstructionsEU}
                onChange={(e) => setFormData({ ...formData, workOrderInstructionsEU: e.target.value })}
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>
                WorkOrder Instructions NA <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter WorkOrder Instructions NA"
                value={formData.workOrderInstructionsNA}
                onChange={(e) => setFormData({ ...formData, workOrderInstructionsNA: e.target.value })}
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                label="Active"
                checked={formData.isActive}
                onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseModal}>
              Cancel
            </Button>
            <Button variant="primary" type="submit">
              {editingCode ? "Update" : "Create"}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
};

export default FaultCodeManagement;
